import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "./LoadingSpinner";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";
import { PenLine } from "lucide-react";

interface WriteFoodModalProps {
  open: boolean;
  onClose: () => void;
  onAnalysisComplete: (detectedFoods: any[], rawResponse: string) => void;
}

export const WriteFoodModal = ({ open, onClose, onAnalysisComplete }: WriteFoodModalProps) => {
  const [description, setDescription] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = async () => {
    const trimmed = description.trim();
    if (!trimmed) {
      toast({ title: "Empty description", description: "Please describe what you ate.", variant: "destructive" });
      return;
    }

    try {
      setIsAnalyzing(true);
      Analytics.foodScanStarted();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('You must be logged in');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-food-text`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ description: trimmed }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      const responseData = await response.json().catch(() => ({ error: 'Unknown error' }));

      if (response.status === 429 || responseData.error === 'daily_limit_reached') {
        Analytics.aiDailyLimitReached();
        toast({
          title: "Daily AI limit reached",
          description: responseData.message || "You've reached today's limit. Try again tomorrow.",
          variant: "destructive"
        });
        return;
      }

      if (!response.ok) {
        throw new Error(responseData.error || `Server error: ${response.status}`);
      }

      if (!responseData.detected_foods || responseData.detected_foods.length === 0) {
        Analytics.foodScanFailed('no_foods_detected');
        toast({
          title: "No foods detected",
          description: "The AI couldn't identify any foods from your description. Try being more specific.",
          variant: "destructive"
        });
        return;
      }

      Analytics.foodScanCompleted(responseData.detected_foods.length);
      onAnalysisComplete(responseData.detected_foods, responseData.raw_response);
      handleClose();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        toast({ title: "Timed out", description: "Please try again.", variant: "destructive" });
      } else {
        Analytics.foodScanFailed(error instanceof Error ? error.message : 'unknown_error');
        toast({
          title: "Analysis failed",
          description: error instanceof Error ? error.message : "Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClose = () => {
    setDescription("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            Describe Your Meal
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            placeholder="e.g. 2 scrambled eggs with toast, a banana, and a glass of orange juice"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={2000}
            disabled={isAnalyzing}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Describe what you ate with quantities if possible. The AI will estimate the nutrition.
          </p>

          {isAnalyzing ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <LoadingSpinner size="lg" />
              <p className="text-sm text-muted-foreground">Analyzing your meal...</p>
            </div>
          ) : (
            <Button onClick={handleAnalyze} className="w-full" disabled={!description.trim()}>
              Analyze Food
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
